import React, { useEffect, useState } from 'react';
import { Modal } from '@care/react-component-lib';
import { Button, Typography, Stack } from '@mui/material';
import {
  Icon24CalendarAndAvailability,
  Icon24ResponseTime,
  Icon24Search,
} from '@care/react-embrace-icons';

import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import {
  JOB_SEARCHING_FOR,
  LaunchDarklyEvaluationReasons,
  ScreenName,
  ScreenTemplate,
  UI_FEATURE_FLAGS,
} from '@/constants';
import useFeatureFlag from '@/components/hooks/useFeatureFlag';
import { usePageAttributes } from '@/components/contexts/PageAttributesContext';
import { type ModalVariant } from '@/types/WhenCareModal';
import { useResponsive } from '@/components/hooks/useResponsive';
import { getWhenCareModalVariant } from './helpers/getWhenCareModalVariant';

interface ModalProp {
  enrollmentURL: string;
  modalTitle: string;
  serviceIdForMember: string;
  pageType: string;
}

// Option types
interface VariantOption {
  label: string;
  intent?: string;
  icon?: React.ReactNode;
}

interface ModalVariantConfig {
  title: string;
  subtitle?: string;
  options: VariantOption[];
}

export const WhenCareModal = ({
  enrollmentURL,
  modalTitle,
  pageType,
  serviceIdForMember,
}: ModalProp) => {
  const [open, setOpen] = useState(false);
  const { isSmallViewport } = useResponsive();
  const { segmentNameSingularLowerCase, searchingFor } = usePageAttributes();
  const daycareDirectoryFirstFlag = useFeatureFlag(
    UI_FEATURE_FLAGS.DAYCARE_DIRECTORY_FIRST_FLOW.id
  );

  const isJobSegment = Boolean(searchingFor && searchingFor in JOB_SEARCHING_FOR);

  const MODAL_VARIANTS: Record<ModalVariant, ModalVariantConfig> = {
    default: {
      title: isJobSegment ? 'When do you want a job?' : modalTitle,
      options: [
        {
          label: 'Right now',
          intent: 'rightNow',
          icon: <Icon24ResponseTime />,
        },
        {
          label: 'Within a month',
          intent: 'withInMonth',
          icon: <Icon24CalendarAndAvailability />,
        },
        {
          label: 'Just browsing',
          intent: 'justBrowsing',
          icon: <Icon24Search />,
        },
      ],
    },
    childCareLocation: {
      title: 'How old is your child?',
      subtitle: `Let's find a ${segmentNameSingularLowerCase} for you!`,
      options: [
        { label: '0 – 12 months' },
        { label: '1 – 4 years' },
        { label: '4 – 6 years' },
        { label: '6+ years' },
      ],
    },
    petCare: {
      title: 'How many pets need care?',
      subtitle: 'Tell us about your needs!',
      options: [{ label: '1' }, { label: '2' }, { label: '3+' }],
    },
    seniorCare: {
      title: 'Who needs care?',
      options: [{ label: 'A loved one' }, { label: 'Myself' }],
    },
  };

  useEffect(() => {
    const doesCookieExist = (cookieName: string): boolean => {
      return !document.cookie.split(';').some((cookie) => cookie.trim().startsWith(cookieName));
    };

    if (doesCookieExist('crcmWhenCareModalShown=')) {
      setTimeout(() => {
        setOpen(true);
        const cookieDate = new Date();
        cookieDate.setDate(cookieDate.getDate() + 90);
        document.cookie = `crcmWhenCareModalShown=true; expires=${cookieDate.toUTCString()}; path=/`;
      }, 3000);
    }
  }, []);

  const handleModalButton = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    const amplitudeData = {
      asset_type: 'Lightbox',
      CTA: e.currentTarget.textContent,
      screen_name: ScreenName.SEGMENTATION,
      screen_template: ScreenTemplate.SEGMENTATION,
    };

    trackAmplitudeEvent(AmplitudeEvent.monetateAssetClicked, amplitudeData);

    if (
      daycareDirectoryFirstFlag &&
      daycareDirectoryFirstFlag.reason.kind === LaunchDarklyEvaluationReasons.RULE_MATCH
    ) {
      trackAmplitudeEvent(AmplitudeEvent.testExposure, {
        test_name: UI_FEATURE_FLAGS.DAYCARE_DIRECTORY_FIRST_FLOW.id,
        test_variant: daycareDirectoryFirstFlag.variationIndex,
        service_id_for_member: serviceIdForMember,
        page_template: pageType,
      });
    }

    globalThis.location.href = e.currentTarget.href;
  };

  const handleCloseModal = () => {
    setOpen(false);

    trackAmplitudeEvent(AmplitudeEvent.monetateAssetClicked, {
      asset_type: 'Lightbox',
      CTA: 'X',
      messagingContent: 'Join now',
      screen_name: ScreenName.SEGMENTATION,
      screen_template: ScreenTemplate.SEGMENTATION,
    });
  };

  const ModalVariant = getWhenCareModalVariant(serviceIdForMember);
  const currentVariant = MODAL_VARIANTS[ModalVariant];

  return (
    <Modal
      open={open}
      maxWidth="xl"
      variant={isSmallViewport ? 'default' : 'dynamic'}
      onClose={handleCloseModal}>
      {currentVariant?.subtitle && (
        <Typography mb={1} variant="serifHeadline3" textAlign="center">
          {currentVariant.subtitle}
        </Typography>
      )}
      <Typography variant="display4" component="h3" textAlign="center">
        {currentVariant.title}
      </Typography>
      <Stack mt={4} mx="auto" maxWidth="256px" spacing={2}>
        {currentVariant.options.map((option) => (
          <Button
            key={option.label}
            rel="nofollow"
            component="a"
            size="large"
            variant="outlined"
            fullWidth
            startIcon={option.icon}
            href={`${enrollmentURL}${option.intent ? `&intent=${option.intent}` : ''}`}
            onClick={handleModalButton}>
            {option.label}
          </Button>
        ))}
      </Stack>
    </Modal>
  );
};
